import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Send,
  Lock,
  Unlock,
  Shield,
  User,
  Clock,
  Check,
  CheckCheck,
  X,
  Mail,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import AttachmentPicker, { UploadedAttachment } from "@/components/support/AttachmentPicker";
import AttachmentList, { AttachmentMeta } from "@/components/support/AttachmentList";

export interface ContactConversation {
  id: string;
  user_id: string | null;
  contact_name: string;
  contact_email: string;
  subject: string;
  status: "new" | "in_progress" | "replied" | "closed";
  access_token?: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  unread_admin_count: number;
  unread_user_count: number;
  closed_at: string | null;
  created_at: string;
}

export interface ContactThreadMessage {
  id: string;
  conversation_id: string;
  sender_type: "admin" | "user";
  sender_id: string | null;
  body: string;
  attachments: AttachmentMeta[];
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
}

export const CONTACT_STATUS_LABELS: Record<ContactConversation["status"], string> = {
  new: "Nouveau",
  in_progress: "En cours",
  replied: "Répondu",
  closed: "Clôturé",
};

const statusVariant = (s: ContactConversation["status"]) =>
  s === "new" ? "destructive" : s === "closed" ? "outline" : s === "replied" ? "secondary" : "default";

const fmtDT = (v: string) =>
  new Date(v).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

interface Props {
  conversationId?: string;
  token?: string;
  asAdmin?: boolean;
  onClose?: () => void;
  onConversationChange?: (c: ContactConversation) => void;
}

const ContactThreadWindow = ({
  conversationId,
  token,
  asAdmin = false,
  onClose,
  onConversationChange,
}: Props) => {
  const [conversation, setConversation] = useState<ContactConversation | null>(null);
  const [messages, setMessages] = useState<ContactThreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const call = useCallback(
    async (action: string, extra: Record<string, unknown> = {}) => {
      const { data, error: fnErr } = await supabase.functions.invoke("contact-thread", {
        body: { action, conversation_id: conversationId, token, ...extra },
      });
      if (fnErr) throw new Error(fnErr.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    [conversationId, token],
  );

  const applyConversation = useCallback(
    (c: ContactConversation | null) => {
      if (!c) return;
      setConversation(c);
      onConversationChange?.(c);
    },
    [onConversationChange],
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    call("get")
      .then((data) => {
        if (!mounted) return;
        applyConversation(data.conversation);
        setMessages((data.messages ?? []) as ContactThreadMessage[]);
      })
      .catch((e: Error) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, token]);

  // Realtime : nouveaux messages du fil
  useEffect(() => {
    const id = conversation?.id;
    if (!id) return;
    const channel = supabase
      .channel(`contact-thread-${id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "contact_conversation_messages",
          filter: `conversation_id=eq.${id}`,
        },
        (payload) => {
          const msg = payload.new as unknown as ContactThreadMessage;
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const isClosed = conversation?.status === "closed";

  const handleSend = async () => {
    if (sending) return;
    const body = content.trim();
    if (!body && attachments.length === 0) return;
    setSending(true);
    try {
      const data = await call("send", { content: body, attachments });
      setContent("");
      setAttachments([]);
      applyConversation(data.conversation);
      const msg = data.message as ContactThreadMessage;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Envoi échoué");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (action: "close" | "reopen") => {
    setBusyStatus(true);
    try {
      const data = await call(action);
      applyConversation(data.conversation);
      toast.success(action === "close" ? "Conversation clôturée" : "Conversation rouverte");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action impossible");
    } finally {
      setBusyStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="p-6 text-center space-y-3">
        <p className="text-sm text-muted-foreground">{error || "Conversation introuvable."}</p>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Retour
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full max-w-full overflow-x-hidden">
      {/* En-tête */}
      <div className="flex flex-wrap items-start gap-2 border-b pb-3">
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="md:hidden">
            <X className="w-4 h-4" />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{conversation.subject}</p>
          <p className="text-xs text-muted-foreground truncate">
            {conversation.contact_name} · {conversation.contact_email}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant={statusVariant(conversation.status)}>
            {CONTACT_STATUS_LABELS[conversation.status]}
          </Badge>
          {conversation.user_id ? (
            <Badge variant="secondary" className="gap-1">
              <BadgeCheck className="w-3 h-3" /> Compte lié
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1">
              <Mail className="w-3 h-3" /> Contact externe
            </Badge>
          )}
          {asAdmin && conversation.user_id && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/profil/${conversation.user_id}`} target="_blank" rel="noopener noreferrer">
                Profil
              </a>
            </Button>
          )}
          {!token && (
            <Button
              variant="outline"
              size="sm"
              disabled={busyStatus}
              onClick={() => changeStatus(isClosed ? "reopen" : "close")}
            >
              {busyStatus ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isClosed ? (
                <>
                  <Unlock className="w-3.5 h-3.5 mr-1" /> Rouvrir
                </>
              ) : (
                <>
                  <Lock className="w-3.5 h-3.5 mr-1" /> Clôturer
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto py-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">Aucun message.</p>
        )}
        {messages.map((m) => {
          const mine = asAdmin ? m.sender_type === "admin" : m.sender_type === "user";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm break-words whitespace-pre-wrap ${
                  m.sender_type === "admin"
                    ? "bg-primary/10 border border-primary/20"
                    : "bg-muted border"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1 text-[11px] text-muted-foreground">
                  {m.sender_type === "admin" ? (
                    <Shield className="w-3 h-3" />
                  ) : (
                    <User className="w-3 h-3" />
                  )}
                  <span className="font-medium">
                    {m.sender_type === "admin" ? "Équipe Click&Gard" : conversation.contact_name}
                  </span>
                  <Clock className="w-3 h-3 ml-1" />
                  <span>{fmtDT(m.created_at)}</span>
                </div>
                {m.body && <p>{m.body}</p>}
                {m.attachments?.length > 0 && (
                  <div className="mt-2">
                    <AttachmentList attachments={m.attachments} bucket="contact-attachments" />
                  </div>
                )}
                {mine && (
                  <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                    {m.read_at ? (
                      <>
                        <CheckCheck className="w-3 h-3 text-primary" /> Lu {fmtDT(m.read_at)}
                      </>
                    ) : m.delivered_at ? (
                      <>
                        <CheckCheck className="w-3 h-3" /> Distribué {fmtDT(m.delivered_at)}
                      </>
                    ) : (
                      <>
                        <Check className="w-3 h-3" /> Envoyé
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      {isClosed ? (
        <div className="border-t pt-3 text-center text-sm text-muted-foreground">
          Conversation clôturée — consultation uniquement.
        </div>
      ) : (
        <div className="border-t pt-3 space-y-2 sticky bottom-0 bg-background pb-[env(safe-area-inset-bottom)]">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Écrivez votre message…"
            rows={3}
            className="resize-none"
            disabled={sending}
          />
          <div className="flex items-center justify-between gap-2">
            <AttachmentPicker
              bucket="contact-attachments"
              value={attachments}
              onChange={setAttachments}
              disabled={sending}
              compact
            />
            <Button
              onClick={handleSend}
              disabled={sending || (!content.trim() && attachments.length === 0)}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <Send className="w-4 h-4 mr-1.5" />
              )}
              Envoyer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactThreadWindow;
